/**
 * Knowledge Base API module.
 */

import { apiFetch, apiFetchWithMeta } from './client.js'
import type { KBEntry, KBEntryDetail } from '../types/api.js'

/** Fetch paginated KB entries list. */
export const fetchKBEntries = (params?: {
  type?: string
  status?: string
  search?: string
  sort?: string
  order?: 'asc' | 'desc'
  page?: number
  page_size?: number
}): Promise<{
  data: KBEntry[]
  meta?: { page?: number; page_size?: number; total?: number; has_more?: boolean }
}> => apiFetchWithMeta<KBEntry[]>('/api/v2/kb/entries', params)

/** Fetch a single KB entry with linked items. */
export const fetchKBEntryDetail = (entryId: string): Promise<KBEntryDetail> =>
  apiFetch<KBEntryDetail>(`/api/v2/kb/entries/${encodeURIComponent(entryId)}`)

/** Read stats for a KB entry (read count + recent read events). */
export interface KBEntryReadStats {
  entryId: string
  readCount: number
  recentReads: {
    actor: string | null
    occurredAt: string
  }[]
}

/** Fetch read stats for a KB entry. */
export const fetchKBReadStats = (entryId: string): Promise<KBEntryReadStats> =>
  apiFetch<KBEntryReadStats>(`/api/v2/kb/entries/${encodeURIComponent(entryId)}/reads`)

/** Fetch read counts for multiple KB entries in one batch call. */
export const fetchKBReadCounts = (ids: string[]): Promise<Record<string, number>> => {
  if (ids.length === 0) return Promise.resolve({})
  return apiFetch<Record<string, number>>('/api/v2/kb/entries/read-counts', {
    ids: ids.join(','),
  })
}

/** Per-entry read stats broken down by time window. */
export interface KBReadStatsWindow {
  entryId: string
  title: string
  type: string
  last1d: number
  last7d: number
  last30d: number
  total: number
}

/** Fetch time-windowed read stats for all KB entries. */
export const fetchKBReadStatsWindow = (): Promise<KBReadStatsWindow[]> =>
  apiFetchWithMeta<KBReadStatsWindow[]>('/api/v2/kb/entries/read-stats').then((r) => r.data)
